import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { collection, addDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import theme from "../styles/themes/theme";

const SharePopover = ({ canvas, open, onClose }) => {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [formState, setFormState] = useState(false);
  const [formData, setFormData] = useState({
    yourName: "",
    yourEmail: "",
    toName: "",
    toEmail: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    yourName: false,
    yourEmail: false,
    toName: false,
    toEmail: false,
  });

  // Email validation regex
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: false })); // Clear error on input change
  };

  // Validate and submit form
  const handleShare = async () => {
    setIsSendingEmail(true);

    // Validate inputs
    const newErrors = {
      yourName: formData.yourName.length < 2,
      yourEmail: !validateEmail(formData.yourEmail),
      toName: formData.toName.length < 2,
      toEmail: !validateEmail(formData.toEmail),
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      setIsSendingEmail(false);
      return;
    }

    // Generate email content
    const textMessage = `
      Dear ${formData.toName},
      <p>I found this sign design at alphabetsigns.com and wanted to share it with you.</p>
      <p>Please let me know what you think.</p>
      <p>${formData.yourName} (${formData.yourEmail})</p>
      <p>P.S. ${formData.message || "No additional message."}</p>
    `;

    // Generate canvas screenshot
    const dataURL = canvas.toDataURL({ format: "jpeg" });

    // Prepare Firestore data
    const data = {
      to: [formData.toEmail, "info@alphabetsigns.com"],
      message: {
        subject: "I just shared a sign design with you",
        text: textMessage,
        html: textMessage,
        replyTo: formData.yourEmail,
        attachments: [{ path: dataURL }],
      },
    };

    try {
      const ref = collection(firestore, "sendEmailRequests");
      await addDoc(ref, data);
      setFormState(true);
    } catch (e) {
      console.error("SharePopover: Error sending email request", e);
      alert("Failed to send email. Please try again.");
    }

    setIsSendingEmail(false);
  };

  // Reset form for another email
  const handleResetForm = () => {
    setFormState(false);
    setFormData({
      yourName: "",
      yourEmail: "",
      toName: "",
      toEmail: "",
      message: "",
    });
    setErrors({
      yourName: false,
      yourEmail: false,
      toName: false,
      toEmail: false,
    });
  };

  return (
    <Dialog
      disableScrollLock={true}
      open={open}
      onClose={onClose}
      TransitionComponent="Zoom"
      fullWidth={true}
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          margin: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          }, // 8px, 16px, 24px
        },
      }}
      aria-labelledby="share-dialog-title"
      aria-describedby="share-dialog-description"
    >
      <DialogTitle
        id="share-dialog-title"
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          fontFamily: "Roboto",
          fontWeight: "bold",
          fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        }}
      >
        Share Design
      </DialogTitle>
      <DialogContent dividers>
        <Box
          id="dialogContentText"
          sx={{
            padding: {
              xs: theme.spacing(1.5),
              sm: theme.spacing(2),
              md: theme.spacing(3),
            }, // 12px, 16px, 24px
          }}
        >
          {!formState ? (
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <FormLabel
                sx={{
                  marginBottom: theme.spacing(0.5), // 4px
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                Send From
              </FormLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                }} // 8px, 16px, 24px
                sx={{ marginBottom: theme.spacing(1) }} // 8px
              >
                <TextField
                  id="yourName"
                  label="Your Name"
                  value={formData.yourName}
                  onChange={handleInputChange}
                  error={errors.yourName}
                  helperText={
                    errors.yourName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="yourEmail"
                  label="Your Email"
                  type="email"
                  value={formData.yourEmail}
                  onChange={handleInputChange}
                  error={errors.yourEmail}
                  helperText={errors.yourEmail ? "Valid email is required" : ""}
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <FormLabel
                sx={{
                  marginBottom: theme.spacing(0.5),
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                Send To
              </FormLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                }}
                sx={{ marginBottom: theme.spacing(1) }}
              >
                <TextField
                  id="toName"
                  label="To Name"
                  value={formData.toName}
                  onChange={handleInputChange}
                  error={errors.toName}
                  helperText={
                    errors.toName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="toEmail"
                  label="To Email"
                  type="email"
                  value={formData.toEmail}
                  onChange={handleInputChange}
                  error={errors.toEmail}
                  helperText={errors.toEmail ? "Valid email is required" : ""}
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                id="message"
                label="Message"
                value={formData.message}
                onChange={handleInputChange}
                multiline
                minRows={3}
                maxRows={5}
                variant="outlined"
                sx={{
                  width: "100%",
                  marginBottom: theme.spacing(1),
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  marginBottom: theme.spacing(1),
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                }}
              >
                This design will be emailed to your associate as a JPEG
                attachment.
              </Typography>
              <Button
                variant="contained"
                onClick={handleShare}
                disabled={isSendingEmail}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                  height: { xs: "2rem", sm: "2.5rem", md: "3rem" }, // 32px, 40px, 48px
                }}
              >
                {isSendingEmail ? "Sending..." : "Share"}
              </Button>
            </FormControl>
          ) : (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "success.main",
                  marginBottom: theme.spacing(1),
                  fontFamily: "Roboto",
                  fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                }}
              >
                Success
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  marginBottom: theme.spacing(1),
                  color: "text.primary",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                }}
              >
                A copy of your design has been shared by email with your
                associate.
              </Typography>
              <Button
                variant="contained"
                onClick={handleResetForm}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                  height: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                Send Another Email
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        }}
      >
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePopover;
